import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, TrendingUp, DollarSign, BarChart3, Target,
  Users, Globe, FileText, Play, Pause, Plus, Activity,
  ArrowUpRight, ArrowDownRight, Eye, Zap, Brain,
  MessageSquare, Hash, Newspaper, MapPin, PenTool
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function CMODashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [campaigns, setCampaigns] = useState([]);
  const [subAgents, setSubAgents] = useState([]);
  const [budget, setBudget] = useState(null);
  const [performance, setPerformance] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);

      const [campRes, agentRes, budgetRes, perfRes] = await Promise.allSettled([
        apiClient.get('/api/v1/operator/cmo/campaigns'),
        apiClient.get('/api/v1/operator/cmo/agents'),
        apiClient.get('/api/v1/operator/cmo/budget'),
        apiClient.get('/api/v1/operator/cmo/performance'),
      ]);

      setCampaigns(campRes.status === 'fulfilled' ? campRes.value.data?.campaigns || [] : [
        { id: 'camp_001', name: 'revenue_recovery_20260318_0900', type: 'revenue_recovery', status: 'active', budget: 500, spent: 312, channels: ['reddit', 'content_writer', 'geo_targeting'], impressions: 12400, clicks: 890, conversions: 34, roi: 2.1, created_at: '2026-03-15T09:00:00Z' },
        { id: 'camp_002', name: 'product_launch_smartsensor', type: 'product_launch', status: 'active', budget: 750, spent: 425, channels: ['hackernews', 'content_writer', 'reddit'], impressions: 8200, clicks: 620, conversions: 18, roi: 1.8, created_at: '2026-03-12T14:30:00Z' },
        { id: 'camp_003', name: 'awareness_q1_push', type: 'awareness', status: 'completed', budget: 1000, spent: 980, channels: ['reddit', 'hackernews', 'content_writer', 'geo_targeting', 'distribution'], impressions: 45000, clicks: 3200, conversions: 89, roi: 3.4, created_at: '2026-02-20T10:00:00Z' },
      ]);

      setSubAgents(agentRes.status === 'fulfilled' ? agentRes.value.data?.agents || [] : [
        { type: 'reddit', name: 'Reddit Agent', status: 'active', actions_today: 12, max_actions_hr: 5, last_action: 'Seeded product in r/ecommerce', approval_mode: 'approve' },
        { type: 'hackernews', name: 'Hacker News Agent', status: 'active', actions_today: 3, max_actions_hr: 2, last_action: 'Posted Show HN: Agentic Commerce', approval_mode: 'approve' },
        { type: 'content_writer', name: 'Content Writer Agent', status: 'active', actions_today: 28, max_actions_hr: 30, last_action: 'Generated blog post: "5 Ways AI Agents Transform Inventory"', approval_mode: 'notify' },
        { type: 'geo_targeting', name: 'Geo-Targeting Agent', status: 'active', actions_today: 8, max_actions_hr: 15, last_action: 'Launched NYC merchant hub campaign', approval_mode: 'notify' },
        { type: 'distribution', name: 'Distribution Agent', status: 'active', actions_today: 15, max_actions_hr: 20, last_action: 'Cross-posted to TikTok Shop + Instagram', approval_mode: 'notify' },
      ]);

      setBudget(budgetRes.status === 'fulfilled' ? budgetRes.value.data : {
        total_monthly: 5000, spent_this_month: 2850, remaining: 2150,
        allocation: { reddit: 1000, hackernews: 500, content_writer: 1500, geo_targeting: 1000, distribution: 1000 },
      });

      setPerformance(perfRes.status === 'fulfilled' ? perfRes.value.data : {
        total_impressions: 65600, total_clicks: 4710, total_conversions: 141,
        avg_roi: 2.43, cac: 18.50, ltv: 245.00,
        channel_performance: [
          { channel: 'Reddit', impressions: 22000, clicks: 1800, conversions: 48, roi: 2.8 },
          { channel: 'Hacker News', impressions: 8500, clicks: 720, conversions: 22, roi: 3.1 },
          { channel: 'Content', impressions: 18000, clicks: 1200, conversions: 38, roi: 2.2 },
          { channel: 'Geo-Targeting', impressions: 9100, clicks: 540, conversions: 18, roi: 1.9 },
          { channel: 'Distribution', impressions: 8000, clicks: 450, conversions: 15, roi: 1.7 },
        ],
      });
    } catch (error) {
      console.error('Failed to load CMO data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = async (agentType, currentStatus) => {
    try {
      await apiClient.post(`/api/v1/operator/cmo/agents/${agentType}/${currentStatus === 'active' ? 'pause' : 'resume'}`);
      setSubAgents(prev => prev.map(a => a.type === agentType ? { ...a, status: currentStatus === 'active' ? 'paused' : 'active' } : a));
      toast.success(`${agentType} agent ${currentStatus === 'active' ? 'paused' : 'resumed'}`);
    } catch { toast.error('Failed to toggle agent'); }
  };

  const launchCampaign = async () => {
    try {
      await apiClient.post('/api/v1/operator/cmo/campaign', { campaign_type: 'acquisition', budget: 500 });
      toast.success('Campaign launched — CMO agent delegating to sub-agents');
      loadData();
    } catch { toast.error('Failed to launch campaign'); }
  };

  const agentIcons = { reddit: Hash, hackernews: Newspaper, content_writer: PenTool, geo_targeting: MapPin, distribution: Globe };
  const getAgentIcon = (type) => agentIcons[type] || Megaphone;

  const statusColors = { active: 'bg-[#3EF1C7]/20 text-[#3EF1C7]', paused: 'bg-[#FFC857]/20 text-[#FFC857]', completed: 'bg-[#9CB0C7]/20 text-[#9CB0C7]' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5CFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      {/* Header */}
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">CMO Dashboard</h1>
            <p className="text-sm text-[#9CB0C7]">CMO Agent Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#7A5CFF]/10 border border-[#7A5CFF]/30 rounded-lg">
              <Brain className="w-5 h-5 text-[#7A5CFF]" />
              <span className="text-sm font-medium text-[#7A5CFF]">CMO: Active</span>
            </div>
            <button onClick={() => navigate('/dashboard')} className="text-sm text-[#9CB0C7] hover:text-white">Dashboard</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Impressions', value: performance?.total_impressions?.toLocaleString() || '0', icon: Eye, trend: '+18%' },
            { label: 'Conversions', value: performance?.total_conversions || 0, icon: Target, trend: '+24%' },
            { label: 'Avg ROI', value: `${performance?.avg_roi?.toFixed(1) || 0}x`, icon: TrendingUp, trend: '+0.3x' },
            { label: 'Budget Remaining', value: `$${budget?.remaining?.toLocaleString() || 0}`, icon: DollarSign, trend: `${Math.round(((budget?.remaining || 0) / (budget?.total_monthly || 1)) * 100)}%` },
          ].map((kpi, i) => (
            <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <kpi.icon className="w-5 h-5 text-[#7A5CFF]" />
                <span className="text-xs text-[#3EF1C7] flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" />{kpi.trend}
                </span>
              </div>
              <p className="text-2xl font-bold text-white">{kpi.value}</p>
              <p className="text-xs text-[#9CB0C7] mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0E1426] border border-[#22304A] rounded-lg p-1 w-fit">
          {['overview', 'campaigns', 'agents', 'budget'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab ? 'bg-[#7A5CFF] text-white' : 'text-[#9CB0C7] hover:text-white'}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Channel Performance */}
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />Channel Performance
              </h3>
              <div className="space-y-3">
                {(performance?.channel_performance || []).map((ch, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0B1120] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-white">{ch.channel}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#9CB0C7]">
                      <span>{ch.impressions?.toLocaleString()} imp</span>
                      <span>{ch.conversions} conv</span>
                      <span className="text-[#3EF1C7] font-medium">{ch.roi}x ROI</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Campaigns */}
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#7A5CFF]" />Active Campaigns
                </h3>
                <button onClick={launchCampaign} className="flex items-center gap-1 px-3 py-1.5 bg-[#7A5CFF] text-white text-xs rounded-lg hover:bg-[#6B4FE0]">
                  <Plus className="w-3 h-3" />Launch
                </button>
              </div>
              <div className="space-y-3">
                {campaigns.filter(c => c.status === 'active').map((camp, i) => (
                  <div key={i} className="p-3 bg-[#0B1120] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white">{camp.type.replace(/_/g, ' ')}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[camp.status]}`}>{camp.status}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#9CB0C7]">
                      <span>${camp.spent}/${camp.budget}</span>
                      <span>{camp.conversions} conv</span>
                      <span className="text-[#3EF1C7]">{camp.roi}x ROI</span>
                    </div>
                    <div className="mt-2 w-full bg-[#22304A] rounded-full h-1.5">
                      <div className="bg-[#7A5CFF] rounded-full h-1.5" style={{ width: `${Math.min((camp.spent / camp.budget) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">All Campaigns</h3>
              <button onClick={launchCampaign} className="flex items-center gap-1 px-4 py-2 bg-[#7A5CFF] text-white text-sm rounded-lg hover:bg-[#6B4FE0]">
                <Plus className="w-4 h-4" />New Campaign
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#9CB0C7] border-b border-[#22304A]">
                    <th className="pb-3 pr-4">Campaign</th>
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3 pr-4">Budget</th>
                    <th className="pb-3 pr-4">Impressions</th>
                    <th className="pb-3 pr-4">Conversions</th>
                    <th className="pb-3">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((camp, i) => (
                    <tr key={i} className="border-b border-[#22304A]/50 text-white">
                      <td className="py-3 pr-4 font-medium">{camp.name?.split('_').slice(0, 2).join(' ')}</td>
                      <td className="py-3 pr-4 text-[#9CB0C7]">{camp.type.replace(/_/g, ' ')}</td>
                      <td className="py-3 pr-4"><span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[camp.status]}`}>{camp.status}</span></td>
                      <td className="py-3 pr-4">${camp.spent}/${camp.budget}</td>
                      <td className="py-3 pr-4">{camp.impressions?.toLocaleString()}</td>
                      <td className="py-3 pr-4">{camp.conversions}</td>
                      <td className="py-3 text-[#3EF1C7] font-medium">{camp.roi}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Agents Tab */}
        {activeTab === 'agents' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {subAgents.map((agent, i) => {
              const Icon = getAgentIcon(agent.type);
              return (
                <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${agent.status === 'active' ? 'bg-[#7A5CFF]/20' : 'bg-[#22304A]'}`}>
                        <Icon className={`w-5 h-5 ${agent.status === 'active' ? 'text-[#7A5CFF]' : 'text-[#9CB0C7]'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{agent.name}</p>
                        <p className="text-xs text-[#9CB0C7]">{agent.approval_mode} mode</p>
                      </div>
                    </div>
                    <button onClick={() => toggleAgent(agent.type, agent.status)}
                      className={`p-2 rounded-lg ${agent.status === 'active' ? 'bg-[#3EF1C7]/20 text-[#3EF1C7]' : 'bg-[#22304A] text-[#9CB0C7]'}`}>
                      {agent.status === 'active' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-[#9CB0C7] mb-3 line-clamp-2">{agent.last_action}</p>
                  <div className="flex items-center justify-between text-xs text-[#9CB0C7]">
                    <span>{agent.actions_today} actions today</span>
                    <span>max {agent.max_actions_hr}/hr</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Budget Tab */}
        {activeTab === 'budget' && budget && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#7A5CFF]" />Monthly Budget
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#9CB0C7]">Spent</span>
                    <span className="text-white">${budget.spent_this_month?.toLocaleString()} / ${budget.total_monthly?.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-[#22304A] rounded-full h-3">
                    <div className="bg-[#7A5CFF] rounded-full h-3" style={{ width: `${(budget.spent_this_month / budget.total_monthly) * 100}%` }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#7A5CFF]" />Channel Allocation
              </h3>
              <div className="space-y-3">
                {Object.entries(budget.allocation || {}).map(([ch, amt], i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#9CB0C7] capitalize">{ch.replace(/_/g, ' ')}</span>
                    <span className="text-sm text-white font-medium">${amt.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OpenClaw Command Reference */}
        <div className="mt-8 bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#7A5CFF]" />OpenClaw Commands (Mac Mini)
          </h3>
          <p className="text-xs text-[#9CB0C7] mb-4">Manage your CMO agent from any messaging channel connected to your Mac mini.</p>
          <div className="grid md:grid-cols-2 gap-2 text-xs font-mono">
            {[
              ['/cmo', 'CMO agent status + active campaigns'],
              ['/cmo campaign', 'Launch a new campaign'],
              ['/cmo budget', 'View budget allocation'],
              ['/cmo channels', 'Sub-agent channel status'],
              ['/cmo report', 'Performance report'],
              ['/cmo pause <agent>', 'Pause a sub-agent'],
              ['/cmo resume <agent>', 'Resume a sub-agent'],
            ].map(([cmd, desc], i) => (
              <div key={i} className="flex gap-3 p-2 bg-[#0B1120] rounded">
                <span className="text-[#7A5CFF] whitespace-nowrap">{cmd}</span>
                <span className="text-[#9CB0C7]">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
